/**
 * UpdateEltJobModal Component
 *
 * Modal for updating an existing ELT job.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { QueryBuilder, extractVariablesFromSql } from './QueryBuilder';
import { CronExpressionInput } from './CronExpressionInput';
import { PipelineConfigSection } from './PipelineConfigSection';
import { useUpdateEltJob } from '../hooks/useUpdateEltJob';
import { useDataAgents } from '../../data-agents/hooks/useDataAgents';
import { useDatabaseConnections } from '../../database-connections/hooks/useDatabaseConnections';
import { useManagedSqlDbs } from '../../managed-sql-databases/hooks/useManagedSqlDbs';
import type {
  EltJob,
  EltJobUpdateDTO,
  EltQueryWithLandingDTO,
  EltJobParameter,
  PipelineConfigurationDTO
} from '../../../types/elt-job.types';

interface UpdateEltJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  eltJob: EltJob;
}

interface FormData extends Omit<EltJobUpdateDTO, 'queries' | 'parameters' | 'pipelineConfig' | 'timezone'> {
  queries: EltQueryWithLandingDTO[];
  parameters: EltJobParameter[];
  pipelineConfig: PipelineConfigurationDTO;
  timezone: string;
}

export const UpdateEltJobModal: React.FC<UpdateEltJobModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  eltJob,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [activeSection, setActiveSection] = useState<number>(0);
  const closeAfterSaveRef = useRef(false);

  const { data: agentsData, isLoading: isLoadingAgents } = useDataAgents(tenantId);
  const { data: connectionsData, isLoading: isLoadingConnections } = useDatabaseConnections(tenantId);
  const { data: managedDbsData, isLoading: isLoadingManagedDbs } = useManagedSqlDbs(tenantId, { status: 'available' });

  const dataAgents = agentsData || [];
  const databaseConnections = connectionsData || [];
  const managedSqlDbs = managedDbsData || [];

  // Default landing config for queries that don't have one
  const defaultLandingConfig = {
    schemaName: '',
    tableName: '',
    autoCreateTable: true,
    sourceColumns: [],
    cleanupStrategy: { strategy: 'TRUNCATE' as const },
  };

  // Default pipeline config when not present
  const defaultPipelineConfig: PipelineConfigurationDTO = {
    enabled: true,
    loadBatchSize: 1000,
    errorHandling: 'ABORT',
    stagingSteps: [],
    productionSteps: [],
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      eltJobName: eltJob.eltJobName,
      description: eltJob.description || '',
      isActive: eltJob.isActive,
      dataAgentId: eltJob.dataAgentId,
      dbConnectionId: eltJob.dbConnectionId,
      managedSqlDbId: eltJob.managedSqlDbId,
      cronExpression: eltJob.cronExpression,
      timezone: eltJob.timezone || 'UTC',
      outputFormat: eltJob.outputFormat,
      compression: eltJob.compression,
      queries: eltJob.queries.map((q) => ({
        queryName: q.queryName,
        sqlQuery: q.sqlQuery,
        orderIndex: q.orderIndex,
        isActive: q.isActive,
        landingConfig: q.landingConfig || defaultLandingConfig,
      })),
      parameters: eltJob.parameters || [],
      pipelineConfig: eltJob.pipelineConfig || defaultPipelineConfig,
    },
  });

  useEffect(() => {
    reset({
      eltJobName: eltJob.eltJobName,
      description: eltJob.description || '',
      isActive: eltJob.isActive,
      dataAgentId: eltJob.dataAgentId,
      dbConnectionId: eltJob.dbConnectionId,
      managedSqlDbId: eltJob.managedSqlDbId,
      cronExpression: eltJob.cronExpression,
      timezone: eltJob.timezone || 'UTC',
      outputFormat: eltJob.outputFormat,
      compression: eltJob.compression,
      queries: eltJob.queries.map((q) => ({
        queryName: q.queryName,
        sqlQuery: q.sqlQuery,
        orderIndex: q.orderIndex,
        isActive: q.isActive,
        landingConfig: q.landingConfig || defaultLandingConfig,
      })),
      parameters: eltJob.parameters || [],
      pipelineConfig: eltJob.pipelineConfig || defaultPipelineConfig,
    });
  }, [eltJob, reset]);

  const { mutate: updateJob, isPending } = useUpdateEltJob(
    tenantId,
    eltJob.eltJobId,
    {
      onSuccess: () => {
        setSuccessMessage('ELT job updated successfully!');
        setErrorMessage('');
        if (closeAfterSaveRef.current) {
          setTimeout(() => {
            onClose();
            setSuccessMessage('');
            setActiveSection(0);
          }, 1500);
        }
      },
      onError: (error) => {
        setErrorMessage(error);
        setSuccessMessage('');
      },
    }
  );

  const onSubmit = (data: FormData) => {
    if (data.queries && data.queries.length === 0) {
      setErrorMessage('At least one query is required');
      setActiveSection(4);
      return;
    }

    // Filter parameters to only include those that are actually detected and have a value
    const validParameters = data.parameters.filter(
      (p) => detectedParameters.includes(p.name) && p.defaultValue.trim() !== ''
    );

    setErrorMessage('');
    setSuccessMessage('');
    updateJob({
      ...data,
      // Send null to clear the schedule (triggers DynamoDB REMOVE), or the cron string to set it
      cronExpression: data.cronExpression || null,
      parameters: validParameters.length > 0 ? validParameters : undefined,
    });
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      setSuccessMessage('');
      setActiveSection(0);
      onClose();
    }
  };

  const sections = [
    { title: 'Basic Info', key: 'basic' },
    { title: 'Configuration', key: 'config' },
    { title: 'Schedule', key: 'schedule' },
    { title: 'Output', key: 'output' },
    { title: 'Queries', key: 'queries' },
    { title: 'Parameters', key: 'parameters' },
    { title: 'Pipeline', key: 'pipeline' },
  ];

  // Watch queries to detect parameters
  const queries = watch('queries');
  const parameters = watch('parameters');

  // Detect all parameters from all queries
  const detectedParameters = useMemo(() => {
    const allParams = new Set<string>();
    queries.forEach((query) => {
      const params = extractVariablesFromSql(query.sqlQuery);
      params.forEach((p) => allParams.add(p));
    });
    return Array.from(allParams);
  }, [queries]);

  // Update parameter default value
  const updateParameterDefault = (paramName: string, defaultValue: string) => {
    const existingIndex = parameters.findIndex((p) => p.name === paramName);
    if (existingIndex >= 0) {
      const updated = [...parameters];
      updated[existingIndex] = { name: paramName, defaultValue };
      setValue('parameters', updated);
    } else {
      setValue('parameters', [...parameters, { name: paramName, defaultValue }]);
    }
  };

  // Get parameter default value
  const getParameterDefault = (paramName: string): string => {
    const parameter = parameters.find((p) => p.name === paramName);
    return parameter?.defaultValue || '';
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full">
      <Modal.Header>Update ELT Job</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Section Navigation */}
          <div className="flex border-b border-gray-200">
            {sections.map((section, index) => (
              <button
                key={section.key}
                type="button"
                onClick={() => setActiveSection(index)}
                className={`px-4 py-2 text-sm font-medium ${
                  activeSection === index
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* Section 0: Basic Info */}
          {activeSection === 0 && (
            <div className="space-y-4">
              <FormField label="Job Code" hint="Code cannot be changed after creation">
                <Input
                  value={eltJob.eltJobCode}
                  disabled
                  className="bg-gray-50 font-mono"
                />
              </FormField>

              <FormField
                label="Job Name"
                required
                error={errors.eltJobName?.message}
              >
                <Input
                  {...register('eltJobName', {
                    required: 'Job name is required',
                  })}
                  error={!!errors.eltJobName}
                />
              </FormField>

              <FormField label="Description">
                <Textarea {...register('description')} rows={3} />
              </FormField>

              <FormField label="Status">
                <div className="flex items-center">
                  <Checkbox {...register('isActive')} />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </div>
              </FormField>
            </div>
          )}

          {/* Section 1: Configuration */}
          {activeSection === 1 && (
            <div className="space-y-4">
              <FormField label="Data Agent" required error={errors.dataAgentId?.message}>
                <Select
                  {...register('dataAgentId', { required: 'Data agent is required' })}
                  disabled={isLoadingAgents}
                >
                  <option value="">Select a data agent...</option>
                  {dataAgents.map((agent) => (
                    <option key={agent.dataAgentId} value={agent.dataAgentId}>
                      {agent.dataAgentName} ({agent.dataAgentCode})
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="Database Connection" required error={errors.dbConnectionId?.message}>
                <Select
                  {...register('dbConnectionId', { required: 'Database connection is required' })}
                  disabled={isLoadingConnections}
                >
                  <option value="">Select a database connection...</option>
                  {databaseConnections.map((conn) => (
                    <option key={conn.dbConnectionId} value={conn.dbConnectionId}>
                      {conn.dbConnectionName} ({conn.dbConnectionCode})
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField
                label="Target Database"
                required
                error={errors.managedSqlDbId?.message}
                hint="Select the managed SQL database to load data into"
              >
                <Select
                  {...register('managedSqlDbId', { required: 'Target database is required' })}
                  disabled={isLoadingManagedDbs}
                >
                  <option value="">Select a target database...</option>
                  {managedSqlDbs.map((db) => (
                    <option key={db.managedSqlDbId} value={db.managedSqlDbId}>
                      {db.managedSqlDbName} ({db.managedSqlDbCode})
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
          )}

          {/* Section 2: Schedule */}
          {activeSection === 2 && (
            <Controller
              name="cronExpression"
              control={control}
              render={({ field }) => (
                <CronExpressionInput
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  timezone={watch('timezone')}
                  onTimezoneChange={(tz) => setValue('timezone', tz)}
                />
              )}
            />
          )}

          {/* Section 3: Output */}
          {activeSection === 3 && (
            <div className="space-y-4">
              <FormField label="Output Format" required>
                <Select {...register('outputFormat')}>
                  <option value="csv">CSV</option>
                  <option value="json">JSON</option>
                </Select>
              </FormField>

              <FormField label="Compression" required>
                <Select {...register('compression')}>
                  <option value="zip">ZIP</option>
                  <option value="gzip">GZIP</option>
                </Select>
              </FormField>
            </div>
          )}

          {/* Section 4: Queries */}
          {activeSection === 4 && (
            <Controller
              name="queries"
              control={control}
              render={({ field }) => (
                <QueryBuilder
                  queries={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
          )}

          {/* Section 5: Parameters */}
          {activeSection === 5 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Job Parameters</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Parameters detected from your SQL queries. Set default values that can be overridden at runtime.
                </p>
              </div>

              {detectedParameters.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500">
                    No parameters detected. Use <code className="bg-gray-100 px-1 rounded">{'{{parameterName}}'}</code> syntax in your SQL queries.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {detectedParameters.map((paramName) => (
                    <FormField
                      key={paramName}
                      label={paramName}
                      hint="Default value (optional)"
                    >
                      <Input
                        value={getParameterDefault(paramName)}
                        onChange={(e) => updateParameterDefault(paramName, e.target.value)}
                        placeholder="Enter default value..."
                      />
                    </FormField>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Section 6: Pipeline Configuration */}
          {activeSection === 6 && (
            <Controller
              name="pipelineConfig"
              control={control}
              render={({ field }) => (
                <PipelineConfigSection
                  config={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex gap-2">
              {activeSection > 0 && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setActiveSection(activeSection - 1)}
                >
                  Previous
                </Button>
              )}
              {activeSection < sections.length - 1 && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setActiveSection(activeSection + 1)}
                >
                  Next
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                loading={isPending}
                onClick={() => {
                  closeAfterSaveRef.current = false;
                  handleSubmit(onSubmit)();
                }}
              >
                Save
              </Button>
              <Button
                type="button"
                variant="primary"
                loading={isPending}
                onClick={() => {
                  closeAfterSaveRef.current = true;
                  handleSubmit(onSubmit)();
                }}
              >
                Save and Close
              </Button>
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
